import React, { useState, useEffect } from 'react';
import { X, Search, Music, Plus, Check } from 'lucide-react';
import { supabase } from '../../../backend/services/supabase';
import { toast } from 'sonner';

interface Release {
  id: string;
  title: string;
  artist_id: string;
  artist_name: string;
  artwork_url?: string;
  release_date?: string;
  has_campaigns?: boolean;
}

interface AddReleaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddReleases: (releaseIds: string[]) => void;
  existingReleaseIds?: string[];
}

export const AddReleaseModal: React.FC<AddReleaseModalProps> = ({
  isOpen,
  onClose,
  onAddReleases,
  existingReleaseIds = []
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [releases, setReleases] = useState<Release[]>([]);
  const [selectedReleaseIds, setSelectedReleaseIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchReleases();
    } else {
      // Reset state when modal closes
      setSearchTerm('');
      setSelectedReleaseIds([]);
    }
  }, [isOpen]);

  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      if (isOpen) {
        fetchReleases();
      }
    }, 300);

    return () => clearTimeout(delayedSearch);
  }, [searchTerm]);

  const fetchReleases = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('releases')
        .select(`
          id,
          title,
          artwork_url,
          release_date,
          artist:artists(id, name),
          campaigns:campaigns(count)
        `)
        .order('release_date', { ascending: false })
        .limit(20);

      if (searchTerm) {
        query = query.or(`title.ilike.%${searchTerm}%,artist.name.ilike.%${searchTerm}%`);
      }

      const { data, error } = await query;

      if (error) throw error;

      const formattedReleases = (data || []).map(release => ({
        id: release.id,
        title: release.title,
        artist_id: release.artist?.id || '',
        artist_name: release.artist?.name || 'Unknown Artist',
        artwork_url: release.artwork_url,
        release_date: release.release_date,
        has_campaigns: (release.campaigns as any)?.length > 0
      }));

      setReleases(formattedReleases);
    } catch (error: any) {
      console.error('Error fetching releases:', error);
      toast.error('Failed to load releases');
    } finally {
      setLoading(false);
    }
  };

  const toggleReleaseSelection = (releaseId: string) => {
    setSelectedReleaseIds(prev => 
      prev.includes(releaseId)
        ? prev.filter(id => id !== releaseId)
        : [...prev, releaseId]
    );
  };

  const handleAddReleases = () => {
    if (selectedReleaseIds.length === 0) {
      toast.error('Please select at least one release');
      return;
    }

    onAddReleases(selectedReleaseIds);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Add Releases to Budget Manager
          </h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search releases or artists..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Release List */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : releases.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <Music className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No releases found</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {releases.map(release => {
                const isSelected = selectedReleaseIds.includes(release.id);
                const isExisting = existingReleaseIds.includes(release.id);
                
                return (
                  <button
                    key={release.id}
                    onClick={() => !isExisting && toggleReleaseSelection(release.id)}
                    disabled={isExisting}
                    className={`flex items-start gap-3 p-3 rounded-lg border transition-all ${
                      isExisting
                        ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 opacity-50 cursor-not-allowed'
                        : isSelected
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                        : 'border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600'
                    }`}
                  >
                    <div className="relative flex-shrink-0">
                      {release.artwork_url ? (
                        <img
                          src={release.artwork_url}
                          alt={release.title}
                          className="w-12 h-12 rounded object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded flex items-center justify-center">
                          <Music className="h-6 w-6 text-gray-400" />
                        </div>
                      )}
                      {isSelected && (
                        <div className="absolute -top-1 -right-1 w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center">
                          <Check className="h-3 w-3 text-white" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 text-left">
                      <div className="font-medium text-gray-900 dark:text-white">
                        {release.title}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {release.artist_name}
                      </div>
                      {release.release_date && (
                        <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                          {new Date(release.release_date).toLocaleDateString()}
                        </div>
                      )}
                      {isExisting && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Already added
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {selectedReleaseIds.length} release{selectedReleaseIds.length !== 1 ? 's' : ''} selected
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleAddReleases}
              disabled={selectedReleaseIds.length === 0}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Add {selectedReleaseIds.length || ''} Release{selectedReleaseIds.length !== 1 ? 's' : ''}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

